/**
 * The workspace layer root's interaction layers, above the view and focus
 * (Part 06 decision 3): the agent cursors (z-50, client coordinates) and the
 * summon bar (z-[60]+). The root is `isolate`, so neither covers the header
 * or the panels, which master-layout stacks after it.
 */
import type { RefObject } from "react";
import { AgentCursorLayer } from "@/components/cursors/agent-cursor-layer";
import { workspaceViewAdapter } from "@/lib/workspace-view-adapter";
import { useDesktopMode } from "@/stores/desktop-mode-store";
import { useCursorInputContext } from "./cursor-input-context";
import { useWorkspaceSummon } from "./use-workspace-summon";
import { WorkspaceCursorInputLayer } from "./workspace-cursor-input-layer";
import { shouldRenderWorkspaceInteractionLayers } from "./workspace-layer-policy";

export function WorkspaceInteractionLayers({
  rootRef,
}: {
  rootRef: RefObject<HTMLElement | null>;
}) {
  const adapter = workspaceViewAdapter();
  const summon = useWorkspaceSummon(rootRef, adapter);
  const cursorInputCtx = useCursorInputContext();
  const mode = useDesktopMode((s) => s.mode);
  if (!shouldRenderWorkspaceInteractionLayers(mode)) return null;
  return (
    <>
      <AgentCursorLayer />
      {summon.cursorInput && cursorInputCtx && (
        <WorkspaceCursorInputLayer
          adapter={adapter}
          cursorInput={summon.cursorInput}
          cursorInputCtx={cursorInputCtx}
          value={summon.value}
          onChange={summon.setValue}
          onClose={summon.close}
        />
      )}
    </>
  );
}
